import { useEffect } from 'react';
import { PfmeaItem } from '../types';
import { createEmptyPfmeaItem } from '../services/pfmeaService';

/**
 * Hook to handle importing SC/CC items from DFMEA (stored in sessionStorage)
 * onto the PFMEA editor.
 */
export function useDfmeaImport(
    activeProjectName: string | null,
    setItems: React.Dispatch<React.SetStateAction<PfmeaItem[]>>,
    setPendingChanges: React.Dispatch<React.SetStateAction<Record<string, Partial<PfmeaItem>>>>,
    setFilterSC: (value: 'all' | 'CC' | 'SC' | 'none') => void
) {
    useEffect(() => {
        const importData = sessionStorage.getItem('dfmea_sc_import');
        if (importData && activeProjectName) {
            try {
                const scItems = JSON.parse(importData) as Array<{
                    dfmeaId: string;
                    function: string;
                    failureMode: string;
                    effects: string;
                    severity: number;
                    specialCharacteristic: 'CC' | 'SC';
                    preventionControls: string[];
                    detectionControls: string[];
                }>;

                const newItems: PfmeaItem[] = scItems.map((sc, idx) => ({
                    ...createEmptyPfmeaItem(activeProjectName),
                    id: `PFMEA-SC-${Date.now()}-${idx}`,
                    processFunction: `[DFMEA] ${sc.function}`,
                    failureMode: sc.failureMode,
                    failureEffectEnd: sc.effects,
                    severity: sc.severity,
                    specialCharacteristic: sc.specialCharacteristic,
                    preventionControls: sc.preventionControls || [],
                    detectionControls: sc.detectionControls || [],
                    linkedDfmeaId: sc.dfmeaId,
                    status: 'draft',
                } as PfmeaItem));

                if (newItems.length > 0) {
                    setItems(prev => [...prev, ...newItems]);
                    const newPending: Record<string, Partial<PfmeaItem>> = {};
                    newItems.forEach(item => { newPending[item.id] = item; });
                    setPendingChanges(prev => ({ ...prev, ...newPending }));
                    // Apply SC filter to show imported items
                    setFilterSC('all');
                }

                sessionStorage.removeItem('dfmea_sc_import');
            } catch (e) {
                console.error('Failed to import SC/CC data from DFMEA:', e);
                sessionStorage.removeItem('dfmea_sc_import');
            }
        }
    }, [activeProjectName, setItems, setPendingChanges, setFilterSC]);
}
